/**
 * TRD-07 §3 — every balance constant.
 *
 * In its own module rather than `index.ts` so that `scenario.ts` can read it without an
 * import cycle: index re-exports scenario, and scenario needs BALANCE. TypeScript is
 * perfectly happy with that cycle; ESM resolves it to `undefined` at runtime, which shows
 * up as an inscrutable crash on first load rather than a compile error.
 *
 * A magic number inside a sim system is a bug — it cannot be tuned without a code change
 * and it hides from balance review, which is why TRD-07's acceptance criteria forbid one.
 */
import { UNBOUNDED_WAVE } from '@prism/sim';

export const BALANCE = Object.freeze({
  STARTING_GOLD: 12,
  CORE_MAX_HP: 20,
  PLACEMENTS_PER_ROUND: 2,
  BENCH_SLOTS: 6,
  SHOP_SLOTS: 4,
  REROLL_BASE_COST: 2,
  REROLL_ESCALATION: 1,
  INTEREST_RATE: 0.1,
  INTEREST_CAP: 5,
  SELL_REFUND_RATE: 0.6,
  MERGE_COUNT: 3,
  /** TRD-07 R-07-27 — the board cap that makes composition a decision. */
  MAX_PLACED_TOWERS: 10,
  WAVE_PAYOUT_BASE: 10,
  /** R-02-08 — cost derives from tier. One source of truth. */
  TOWER_COST: Object.freeze({ 1: 3, 2: 5, 3: 8 } as const),
  /** TRD-07 R-07-02 — shop tier weights by wave. */
  SHOP_TIER_WEIGHTS: Object.freeze([
    { maxWave: 3, weights: [100, 0, 0] },
    { maxWave: 7, weights: [70, 30, 0] },
    { maxWave: 12, weights: [45, 42, 13] },
    { maxWave: 18, weights: [25, 45, 30] },
    // Not Infinity — this config is serialized to the client as JSON, where Infinity
    // becomes null. See UNBOUNDED_WAVE in @prism/sim.
    { maxWave: UNBOUNDED_WAVE, weights: [15, 40, 45] },
  ] as const),
  // R-06-10's global HP curve does NOT live here. It is `waveHpScale` in @prism/sim, and
  // this held a second copy of it as a linear rate — which stayed 0.11 when the real curve
  // became geometric, so all three measurement tools spent a release reporting wave health
  // that no run has ever faced. One curve, in the package that applies it.
  /** TRD-06 R-06-09 — one draw per wave, ±8%. */
  WAVE_HP_JITTER: 0.08,
  /** TRD-06 R-06-19 / R-06-24 — hard caps. */
  MAX_DRONES: 80,
  MAX_ENEMIES: 400,
});

export function towerCost(tier: 1 | 2 | 3): number {
  return BALANCE.TOWER_COST[tier];
}
